import { useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface NuancedFilterRequest {
  content: string;
  platform: string;
  author?: string;
  hashtags?: string[];
  metadata?: any;
  content_id?: string;
  context?: {
    time_of_day?: string;
    user_engagement_history?: any;
    content_frequency?: number;
    platform_specific_context?: any;
  };
}

interface IntentAnalysis {
  primary_intent: string;
  intent_confidence: number;
  commercial_score: number;
  manipulation_score: number;
  engagement_intent: number;
}

interface ContextFactors {
  time_relevance: number;
  platform_appropriateness: number;
  user_behavior_pattern: number;
}

interface RuleContribution {
  rule_id: string;
  rule_name: string;
  base_score: number;
  weighted_score: number;
  context_modifier: number;
}

interface NuancedFilterResult {
  shouldFilter: boolean;
  confidence: number;
  weighted_score: number;
  intent_analysis: IntentAnalysis;
  context_factors: ContextFactors;
  rule_contributions: RuleContribution[];
  ai_insights: any;
  reasons: string[];
}

export const useNuancedContentFilter = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const analyzeContent = async (request: NuancedFilterRequest): Promise<NuancedFilterResult | null> => {
    if (!user) {
      setError('User not authenticated');
      return null;
    }

    setLoading(true);
    setError(null);

    try {
      const { data, error: functionError } = await supabase.functions.invoke('nuanced-content-filter', {
        body: {
          ...request,
          user_id: user.id,
          context: {
            time_of_day: new Date().toISOString(),
            ...request.context
          }
        }
      });

      if (functionError) {
        throw new Error(functionError.message);
      }

      if (!data.success) {
        throw new Error(data.error || 'Analysis failed');
      }

      return {
        shouldFilter: data.shouldFilter,
        confidence: data.confidence,
        weighted_score: data.weighted_score,
        intent_analysis: data.intent_analysis,
        context_factors: data.context_factors,
        rule_contributions: data.rule_contributions,
        ai_insights: data.ai_insights,
        reasons: data.reasons
      };
    } catch (err: any) {
      setError(err.message || 'Failed to analyze content');
      return null;
    } finally {
      setLoading(false);
    }
  };

  const batchAnalyze = async (
    requests: NuancedFilterRequest[],
    onProgress?: (completed: number, total: number) => void
  ): Promise<NuancedFilterResult[]> => {
    const results: NuancedFilterResult[] = [];
    
    for (let i = 0; i < requests.length; i++) {
      const result = await analyzeContent(requests[i]);
      if (result) {
        results.push(result);
      }
      onProgress?.(i + 1, requests.length);
    }

    return results;
  };

  return {
    analyzeContent,
    batchAnalyze,
    loading,
    error
  };
};